/*
 * Load more pagination for collection pages (markup in snippets/load-more-pagination.liquid).
 *
 * - Clicking "Load more" fetches the next page of the section (same filters and sort, via the link's URL), appends
 *   its product cards to the existing product list and shows that page in the URL (?page=3). The URL is replaced, not
 *   added to the history, so the back button still leaves the collection in one step.
 * - Shopify only renders the page in the URL, so when a page after the first one is opened (directly, on refresh or
 *   when coming back), the pages before it are added above, so the visitor always sees the products from page 1.
 * - Which product is at the top of the screen is saved in the browser history entry (history.state). This works in
 *   every browser, private mode included, and survives the back button and a refresh.
 * - The inline script in the snippet has already added grey placeholders (and, when coming back, jumped to the right
 *   place) before the page is shown. This file fetches the products and swaps them in, keeping the screen steady.
 */
(() => {
  if (window.customElements.get('load-more-pagination')) {
    return;
  }

  const MAX_PAGES = 20; // Most pages shown at once when a page is opened directly or restored (keep in sync with the snippet)
  const SAVE_INTERVAL = 1000; // Browsers limit how often history.replaceState can be called
  const HEADER_OFFSET = 100;
  const INTERACTION_EVENTS = ['wheel', 'touchstart', 'keydown', 'mousedown'];
  const LINK_SAVE_LOCK = 3000; // After a link is followed, keep that position rather than later saves

  // The wishlist script only marks the hearts present on page load, so we mark the newly added ones ourselves
  const syncWishlistButtons = (cards) => {
    let handles;

    try {
      handles = new Set((JSON.parse(localStorage.getItem('wishlist')) || []).map((item) => item?.handle));
    } catch (error) {
      return;
    }

    cards.forEach((card) => {
      card.querySelectorAll('.custom-wishlist-btn').forEach((button) => {
        button.classList.toggle('added', handles.has(button.dataset.productHandle));
      });
    });
  };

  // How this page was opened: "navigate" (link, typed URL), "reload" or "back_forward"
  const getNavigationType = () => {
    try {
      const entry = performance.getEntriesByType('navigation')[0];

      if (entry) {
        return entry.type;
      }

      const legacyType = performance.navigation?.type;
      return legacyType === 1 ? 'reload' : legacyType === 2 ? 'back_forward' : 'navigate';
    } catch (error) {
      return 'navigate';
    }
  };

  // Collection URL without the page number (filters and sort included). Template test parameters (Intelligems adds
  // ?view=...&ig-redirect-session=...) are left out too, so the saved position still matches after its reload.
  const getBaseUrl = () => {
    const url = new URL(window.location.href);
    ['page', 'section_id', 'view', 'ig-redirect-session'].forEach((name) => url.searchParams.delete(name));
    return url.pathname + url.search;
  };

  // Pages to show: from page 1 up to the page in the URL (or further if more were loaded before a refresh / back),
  // at most MAX_PAGES of them. Same calculation as the inline script in the snippet.
  const getPageRange = (urlPage, savedEndPage, totalPages) => {
    let lastPage = Math.max(urlPage, Math.min(savedEndPage || 0, totalPages));
    const firstPage = Math.max(1, Math.min(urlPage, lastPage - MAX_PAGES + 1));
    lastPage = Math.min(lastPage, firstPage + MAX_PAGES - 1);

    return { firstPage, lastPage };
  };

  class LoadMorePagination extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section');
      this.productList = this.section?.querySelector('product-list');

      if (!this.section || !this.productList) {
        return;
      }

      this.sectionId = this.section.id.replace('shopify-section-', '');
      this.abortController = new AbortController();
      const signal = this.abortController.signal;

      this.addEventListener('click', this.onClick.bind(this), { signal });
      window.addEventListener('scroll', this.scheduleSave.bind(this), { passive: true, signal });
      window.addEventListener('pagehide', () => this.saveState(), { signal });

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.saveState();
        }
      }, { signal });

      // Save right away when any link is followed (Safari does not reliably let us save on "pagehide"). When it is a
      // product, that product becomes the one to come back to.
      document.addEventListener('click', (event) => {
        const link = event.target.closest('a[href]');

        if (link && !link.hasAttribute('data-load-more-button')) {
          const card = this.productList.contains(link) ? link.closest('product-card') : null;
          this.saveState({ anchorElement: card, fromLink: true });
        }
      }, { capture: true, signal });

      // Safari (and sometimes Chrome) keep the page in memory and show it again as it was on back: none of our code
      // runs again, and the browser does not put the scroll position back, so we do it here
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) {
          this.onPageRestoredFromCache();
        }
      }, { signal });

      this.restore();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      clearTimeout(this.saveTimeout);
    }

    // The page Shopify rendered (the page in the URL)
    get startPage() {
      return parseInt(this.getAttribute('start-page'), 10) || 1;
    }

    // The last page shown
    get currentPage() {
      return parseInt(this.getAttribute('current-page'), 10) || 1;
    }

    get totalPages() {
      const pageSize = parseInt(this.getAttribute('page-size'), 10) || 1;
      return Math.ceil((parseInt(this.getAttribute('total'), 10) || 0) / pageSize);
    }

    get button() {
      return this.querySelector('[data-load-more-button]');
    }

    get stateKey() {
      return `${this.section.id}:${getBaseUrl()}`;
    }

    get userScrolled() {
      return this.hasAttribute('user-scrolled');
    }

    getPageUrl(page) {
      const url = new URL(window.location.href);
      url.searchParams.set('page', page);
      url.searchParams.delete('section_id');
      return url.toString();
    }

    /* ---------------------------------------------------------------------------------------------------------------
     * Load more
     * ------------------------------------------------------------------------------------------------------------- */

    async onClick(event) {
      const button = event.target.closest('[data-load-more-button]');

      if (!button) {
        return;
      }

      event.preventDefault();

      if (this.isLoading) {
        return;
      }

      const fromKeyboard = event.detail === 0;
      this.setLoading(true);

      try {
        const newCards = this.appendPage(await this.fetchPage(button.href));
        this.saveState();

        // Keyboard and screen reader users are moved to the first new product instead of staying on the button
        if (fromKeyboard) {
          newCards[0]?.querySelector('a[href]')?.focus();
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          window.location.href = button.href; // Fall back to a normal page load
        }
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(isLoading) {
      this.isLoading = isLoading;
      this.button?.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    async fetchPage(pageUrl) {
      const url = new URL(pageUrl, window.location.origin);
      const templateSuffix = this.getAttribute('template-suffix');
      url.searchParams.set('section_id', this.sectionId);

      // Always ask for the template this page uses (its sections only exist there), even when a template test
      // (Intelligems) has removed ?view= from the URL
      if (templateSuffix) {
        url.searchParams.set('view', templateSuffix);
      }

      const response = await fetch(url.toString(), { signal: this.abortController.signal });

      if (!response.ok) {
        throw new Error(`Load more: request failed with status ${response.status}`);
      }

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

      if (!doc.querySelector('product-list')) {
        throw new Error('Load more: no product list in response');
      }

      return doc;
    }

    /*
     * Adds the products of a fetched page: in place of that page's placeholders when there are some, otherwise before
     * "insertBefore" (pages before the one in the URL) or at the end. Only a page after the last one shown updates the
     * text, the bar and the button.
     */
    appendPage(doc, page = null, insertBefore = null) {
      const existingHandles = new Set(Array.from(this.productList.querySelectorAll('product-card[handle]'), (card) => card.getAttribute('handle')));
      const placeholders = page ? this.productList.querySelectorAll(`.load-more__placeholder[data-page="${page}"]`) : [];
      const fragment = document.createDocumentFragment();
      const newCards = [];

      Array.from(doc.querySelector('product-list').children).forEach((item) => {
        const handle = item.getAttribute('handle');

        // If products moved between two pages since the first one loaded, never show the same product twice
        if (handle && existingHandles.has(handle)) {
          return;
        }

        const card = document.importNode(item, true);
        card.removeAttribute('reveal-on-scroll'); // The theme only reveals cards present on page load
        card.classList.add('load-more__new-item');
        fragment.appendChild(card);
        newCards.push(card);
      });

      if (placeholders.length) {
        placeholders[0].before(fragment);
        placeholders.forEach((placeholder) => placeholder.remove());
      } else if (insertBefore?.isConnected) {
        insertBefore.before(fragment);
      } else {
        this.productList.appendChild(fragment);
      }

      syncWishlistButtons(newCards);

      if (page === null || page > this.currentPage) {
        this.updateControls(doc.querySelector('load-more-pagination'));
      }

      this.dispatchEvent(new CustomEvent('load-more:loaded', {
        bubbles: true,
        detail: { cards: newCards, page: page ?? this.currentPage }
      }));

      return newCards;
    }

    // Update the text, the bar and the button from the freshly rendered page
    updateControls(newElement) {
      if (!newElement) {
        return;
      }

      this.setAttribute('current-page', newElement.getAttribute('current-page'));

      const summary = this.querySelector('.load-more__summary');
      const newSummary = newElement.querySelector('.load-more__summary');

      if (summary && newSummary) {
        summary.innerHTML = newSummary.innerHTML;
      }

      const progressBar = this.querySelector('.load-more__progress');
      const newProgressBar = newElement.querySelector('.load-more__progress');

      if (progressBar && newProgressBar) {
        progressBar.setAttribute('aria-valuemax', newProgressBar.getAttribute('aria-valuemax'));
        progressBar.setAttribute('aria-valuenow', newProgressBar.getAttribute('aria-valuenow'));
        progressBar.style.setProperty('--progress', newProgressBar.style.getPropertyValue('--progress'));
      }

      const newButton = newElement.querySelector('[data-load-more-button]');

      if (newButton) {
        this.button?.setAttribute('href', newButton.getAttribute('href'));
      } else {
        this.button?.remove(); // Last page reached
      }
    }

    /* ---------------------------------------------------------------------------------------------------------------
     * Saving the position (history.state and ?page= in the URL)
     * ------------------------------------------------------------------------------------------------------------- */

    // Where an item of the list is: its index, its product handle and its distance from the top of the screen
    getAnchorFor(element) {
      return {
        anchorIndex: Array.prototype.indexOf.call(this.productList.children, element),
        anchorHandle: element.getAttribute('handle'),
        anchorOffset: element.getBoundingClientRect().top
      };
    }

    // The product at the top of the screen
    getViewportAnchor() {
      const items = Array.from(this.productList.children);
      const index = items.findIndex((item) => item.getBoundingClientRect().bottom > HEADER_OFFSET);
      const anchor = items[index === -1 ? items.length - 1 : index];

      return anchor ? this.getAnchorFor(anchor) : { anchorIndex: 0, anchorHandle: null, anchorOffset: 0 };
    }

    scheduleSave() {
      if (this.isRestoring || this.saveTimeout) {
        return;
      }

      const wait = Math.max(300, SAVE_INTERVAL - (Date.now() - (this.lastSave || 0)));
      this.saveTimeout = setTimeout(() => {
        this.saveTimeout = null;
        this.saveState();
      }, wait);
    }

    saveState({ anchorElement = null, fromLink = false } = {}) {
      // While products are being put back, only a tap on a link is worth saving (positions are still moving)
      if (!this.isConnected || (this.isRestoring && !fromLink)) {
        return;
      }

      // A link was just followed: keep the position saved at that moment, later saves (page hide...) can't improve it
      if (!fromLink && Date.now() < (this.linkSaveTime || 0) + LINK_SAVE_LOCK) {
        return;
      }

      const endPage = Math.max(this.currentPage, this.isRestoring ? this.restoreTargetPage : 0);

      // Only page 1 on screen: nothing to remember, the browser restores the position on its own
      if (endPage <= 1) {
        return;
      }

      if (fromLink) {
        this.linkSaveTime = Date.now();
        clearTimeout(this.saveTimeout);
        this.saveTimeout = null;
      }

      try {
        history.replaceState({
          ...(history.state || {}),
          loadMore: {
            key: this.stateKey,
            url: getBaseUrl(),
            endPage,
            ...(anchorElement ? this.getAnchorFor(anchorElement) : this.getViewportAnchor())
          }
        }, '', this.getPageUrl(endPage));

        this.lastSave = Date.now();

        // The page is shorter when it reloads, so the browser can't restore the position itself: we take over
        if ('scrollRestoration' in history) {
          history.scrollRestoration = 'manual';
        }
      } catch (error) {
        // Called too often (browser limit): the next save will catch up
      }
    }

    /* ---------------------------------------------------------------------------------------------------------------
     * Restoring (page opened directly, back button, refresh)
     * ------------------------------------------------------------------------------------------------------------- */

    getSavedState() {
      const state = history.state?.loadMore;
      return state && state.key === this.stateKey ? state : null;
    }

    // Shopify only rendered the page in the URL: fetch the pages before it (and after it if more were loaded before)
    async restore() {
      const navigationType = getNavigationType();
      const isReturning = navigationType === 'back_forward' || navigationType === 'reload';

      // Clicking a link to this same page reuses its history entry, saved state included: that visit starts fresh
      if (!isReturning && history.state?.loadMore) {
        const { loadMore, ...otherState } = history.state;

        try {
          history.replaceState(otherState, '');
        } catch (error) {
          // Ignore: the state is simply not used
        }
      }

      // Worked out by the script at the top of layout/theme.liquid, before a template test (Intelligems) can clear it.
      // Used once: a later re-render of the section (filters...) is not a return.
      const returnState = window.loadMoreReturnState !== undefined
        ? window.loadMoreReturnState
        : (isReturning ? history.state?.loadMore : null);
      const state = returnState && returnState.key === this.stateKey ? returnState : null;
      window.loadMoreReturnState = null;

      const { firstPage, lastPage } = getPageRange(this.startPage, state?.endPage, this.totalPages);

      if (this.startPage > this.totalPages || (firstPage === this.startPage && lastPage <= this.currentPage)) {
        this.removePlaceholders();

        // Nothing of ours to restore (e.g. filters changed after loading more): let the browser handle scrolling again
        if ('scrollRestoration' in history) {
          history.scrollRestoration = 'auto';
        }

        return;
      }

      if (!state) {
        this.addPreviousPages(firstPage);
        return;
      }

      this.isRestoring = true;
      this.restoreTargetPage = lastPage;
      this.setLoading(true);
      this.productList.style.overflowAnchor = 'none'; // We keep the position ourselves

      const markScrolled = () => this.setAttribute('user-scrolled', '');
      INTERACTION_EVENTS.forEach((name) => window.addEventListener(name, markScrolled, { passive: true, once: true, signal: this.abortController.signal }));

      // First product of the page Shopify rendered: the pages before it go above it
      const firstRenderedCard = this.productList.querySelector(':scope > product-card');
      const pages = [];

      for (let page = firstPage; page <= lastPage; page++) {
        if (page !== this.startPage) {
          pages.push({ page, request: this.fetchPage(this.getPageUrl(page)).catch((error) => error) }); // Fetched in parallel
        }
      }

      for (const { page, request } of pages) {
        const doc = await request; // ...and added in order

        if (doc instanceof Error) {
          if (page < this.startPage) {
            this.keepPosition(state, () => this.productList.querySelectorAll(`.load-more__placeholder[data-page="${page}"]`).forEach((placeholder) => placeholder.remove()));
            continue; // A page above could not be loaded: skip it, the others still come in
          }

          break; // Keep what loaded, the button carries on from there
        }

        this.keepPosition(state, () => this.appendPage(doc, page, page < this.startPage ? firstRenderedCard : null));
      }

      this.keepPosition(state, () => this.removePlaceholders());
      this.setLoading(false);
      this.isRestoring = false;

      if (state) {
        this.stabilize(this.getSavedState() || state); // A product may have been tapped in the meantime
      } else {
        this.productList.style.overflowAnchor = '';
        this.saveState();
      }
    }

    /*
     * A page after the first one was opened directly (?page=5): its products are already shown as Shopify sent them.
     * The pages before it are fetched in the background and, once they have all arrived, added above in one go. If the
     * visitor has scrolled meanwhile, what they are looking at stays in place; otherwise they now see page 1 at the top.
     */
    async addPreviousPages(firstPage) {
      const firstRenderedCard = this.productList.querySelector(':scope > product-card');
      const pages = [];

      for (let page = firstPage; page < this.startPage; page++) {
        pages.push(page);
      }

      const docs = await Promise.all(pages.map((page) => this.fetchPage(this.getPageUrl(page)).catch((error) => error)));

      if (!this.isConnected) {
        return;
      }

      const root = document.documentElement;
      const anchor = window.scrollY > 0 ? this.getViewportAnchorElement() : null;
      const anchorTop = anchor?.getBoundingClientRect().top;

      root.style.overflowAnchor = 'none'; // We keep the position ourselves (only once, instead of the browser guessing)

      docs.forEach((doc, index) => {
        if (!(doc instanceof Error)) {
          this.appendPage(doc, pages[index], firstRenderedCard);
        }
      });

      if (anchor) {
        window.scrollBy(0, anchor.getBoundingClientRect().top - anchorTop);
      }

      root.style.overflowAnchor = '';
      this.saveState();
    }

    // The product (or other item of the list) at the top of the screen
    getViewportAnchorElement() {
      return Array.from(this.productList.children).find((item) => item.getBoundingClientRect().bottom > HEADER_OFFSET) || null;
    }

    // The browser showed the page from its memory (all products are still there): only the position is missing
    onPageRestoredFromCache() {
      const state = this.getSavedState();

      if (!state || this.isRestoring) {
        return;
      }

      this.removeAttribute('user-scrolled');
      this.productList.style.overflowAnchor = 'none';
      this.pin(state);
      this.stabilize(state);
    }

    removePlaceholders() {
      this.productList.querySelectorAll('.load-more__placeholder').forEach((placeholder) => placeholder.remove());
      document.body.style.minHeight = '';
      this.removeAttribute('restoring');
    }

    // The saved product when it is on the page, otherwise whatever is at its position (e.g. a placeholder)
    findAnchor(state) {
      const items = this.productList.children;
      const byHandle = state.anchorHandle
        ? this.productList.querySelector(`product-card[handle="${CSS.escape(state.anchorHandle)}"]`)
        : null;

      return byHandle || items[Math.min(state.anchorIndex || 0, items.length - 1)];
    }

    // Puts the saved product back at the saved distance from the top of the screen
    pin(state) {
      const anchor = this.findAnchor(state);

      if (anchor) {
        window.scrollTo(0, anchor.getBoundingClientRect().top + window.scrollY - (state.anchorOffset || 0));
      }
    }

    // Runs a change to the list without moving what the visitor is looking at
    keepPosition(state, change) {
      if (state && !this.userScrolled) {
        change();
        this.pin(state);
        return;
      }

      // Page opened directly, or the visitor already scrolled: keep whatever is at the top of their screen in place
      const { anchorIndex, anchorOffset } = this.getViewportAnchor();
      change();
      const anchor = this.productList.children[anchorIndex];

      if (anchor) {
        window.scrollBy(0, anchor.getBoundingClientRect().top - anchorOffset);
      }
    }

    // Review stars (Loox) are added a moment after the products and make rows taller. For a few seconds, keep the
    // product where it was whenever the list changes height, unless the visitor starts scrolling themselves.
    stabilize(state) {
      let resizeObserver = null;
      let timeout = null;

      const finish = () => {
        resizeObserver?.disconnect();
        clearTimeout(timeout);
        INTERACTION_EVENTS.forEach((name) => window.removeEventListener(name, finish));
        this.productList.style.overflowAnchor = '';
        this.saveState();
      };

      if (this.userScrolled || !('ResizeObserver' in window)) {
        finish();
        return;
      }

      INTERACTION_EVENTS.forEach((name) => window.addEventListener(name, finish, { passive: true }));
      resizeObserver = new ResizeObserver(() => this.pin(state));
      resizeObserver.observe(this.productList);
      timeout = setTimeout(finish, 4000);
    }
  }

  window.customElements.define('load-more-pagination', LoadMorePagination);
})();
